import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Ruler, ShieldCheck, Layers, Download, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LANGUAGES } from "@/lib/i18n";
import { SAMPLE_MODEL } from "@/lib/sampleModel";
import { AutoBIMLogo, Wordmark } from "@/components/Brand";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import GooglePlayButton from "@/components/GooglePlayButton";
import PlanSVG from "@/components/PlanSVG";
import { Button, Card } from "@/components/ui";

const TYPOLOGIES = [
  ["🏡", "Villa", "فيلا"], ["🏢", "Apartment", "شقة"], ["🏨", "Studios", "استوديوهات"],
  ["🏛", "Office", "مكاتب"], ["🛏", "Hotel", "فندق"], ["🛍", "Shops", "محلات"],
  ["🏬", "Mall", "مول"], ["🏊", "Pool", "مسبح"], ["🕌", "Mosque", "مسجد"],
  ["🏫", "School", "مدرسة"], ["🏥", "Hospital", "مستشفى"], ["🏭", "Factory", "مصنع"],
  ["🏠", "Duplex", "دوبلكس"], ["🏙", "Mixed-use", "متعدد الاستخدام"],
];
const CODES = ["SBC 304 · KSA", "Eurocode 2 · EU", "IBC 2021 · US", "BS 8110 · UK", "IS 456 · India", "ECP 203 · Egypt"];
const EXPORTS = ["DXF", "PDF", "IFC", "XLSX", "SVG"];
const PLAY_URL = "https://play.google.com/store/apps/details?id=com.autobimlogic.app";

// Condensed FAQ (en/ar) - indexable Q&A for the Showcase page.
const FAQS = [
  ["Can I open the plans in AutoCAD or Revit?", "هل يمكن فتح المخططات في AutoCAD أو Revit؟",
   "Yes. Export DXF for AutoCAD and IFC for Revit and other BIM tools - every wall and column is millimetre-accurate.",
   "نعم. صدّر DXF لأوتوكاد وIFC لـ Revit وأدوات BIM الأخرى - كل جدار وعمود بدقّة المليمتر."],
  ["Which building codes are checked?", "ما الأكواد التي يتم فحصها؟",
   "Plans can be validated against SBC (KSA), Eurocode, IBC (US), BS, IS and ECP so layouts follow local regulations.",
   "يمكن فحص المخططات وفق SBC السعودي واليوروكود وIBC الأمريكي وBS وIS وECP لتتبع الأنظمة المحلية."],
  ["What deliverables do I get?", "ما المخرجات التي أحصل عليها؟",
   "Floor plans, elevations, sections, foundations, rebar/door/window schedules, MEP layouts, 3D IFC and a BOQ.",
   "مساقط وواجهات وقطاعات وأساسات وجداول تسليح وأبواب ونوافذ وأنظمة MEP ونموذج ثلاثي الأبعاد وحصر كميات."],
  ["Is it free and multilingual?", "هل هو مجاني ومتعدد اللغات؟",
   "Yes - currently free with unlimited projects, and the whole product works in 15 languages with full RTL support.",
   "نعم - مجاني حالياً بمشاريع بلا حدود، والمنتج بالكامل يعمل بـ15 لغة مع دعم كامل للاتجاه من اليمين لليسار."],
];

export default function Showcase() {
  const { user, lang } = useAuth();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  useEffect(() => { document.title = isAr ? "معرض AutoBIM Logic - نماذج المخططات والأكواد" : "AutoBIM Logic Showcase - Sample Plans & Codes"; }, [isAr]);

  return (
    <div className="min-h-screen abl-grid-bg">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/85 px-5 py-3 backdrop-blur">
        <Link to="/landing" className="flex items-center gap-2.5"><AutoBIMLogo size={32} /><Wordmark /></Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher compact />
          <Button size="sm" onClick={() => navigate(user ? "/studio" : "/register")} data-testid="showcase-cta">
            {user ? (isAr ? "الاستوديو" : "Studio") : isAr ? "جرّب مجاناً" : "Try free"} <ArrowRight size={14} />
          </Button>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-5 py-12 text-center">
        <p className="abl-eyebrow mb-2">{isAr ? "المعرض" : "Showcase"}</p>
        <h1 className="mx-auto max-w-3xl text-3xl font-black leading-tight sm:text-5xl">{isAr ? "مخططات هندسية حقيقية - بدقة أوتوكاد" : "Real engineered plans - AutoCAD-grade"}</h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">{isAr ? "كل مخطط يُولَّد بجدران وأبواب ونوافذ وأعمدة بأبعاد حقيقية بالمليمتر، ويُصدَّر مباشرة إلى AutoCAD وRevit." : "Every plan is generated with millimetre-accurate walls, doors, windows and columns - and exports straight to AutoCAD and Revit."}</p>
        <div className="mt-10 grid items-center gap-8 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-border bg-[#0B0D11] text-start shadow-2xl">
            <div className="flex items-center gap-2 border-b border-border bg-[#141821] px-4 py-2.5">
              <span className="h-3 w-3 rounded-full bg-red-400/80" /><span className="h-3 w-3 rounded-full bg-amber-400/80" /><span className="h-3 w-3 rounded-full bg-emerald-400/80" />
            </div>
            <div className="bg-white"><PlanSVG model={SAMPLE_MODEL} w={640} h={440} showScale showLegend /></div>
          </div>
          <div className="text-start">
            {[
              [Ruler, isAr ? "دقة المليمتر" : "Millimetre precision", isAr ? "جدران بسماكة حقيقية وأبواب بأقواس فتح ونوافذ زجاجية مزدوجة." : "Real wall thickness, door swing arcs and double-glazed windows."],
              [ShieldCheck, isAr ? "6 أكواد عالمية" : "6 global codes", isAr ? "SBC · Eurocode · IBC · BS · IS · ECP - كل قرار مبرَّر هندسياً." : "SBC · Eurocode · IBC · BS · IS · ECP - every decision justified."],
              [Layers, isAr ? "مخرجات كاملة" : "Full deliverables", isAr ? "مساقط وواجهات وقطاعات وأساسات وجداول وMEP بضغطة واحدة." : "Plans, elevations, sections, foundations, schedules & MEP in one click."],
            ].map(([Icon, t, b], i) => (
              <div key={i} className="mb-4 flex items-start gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/15"><Icon className="text-primary" size={20} /></div>
                <div><div className="font-black">{t}</div><p className="text-sm text-muted-foreground">{b}</p></div>
              </div>
            ))}
            <div className="mt-4 flex flex-wrap gap-3">
              <Button onClick={() => navigate(user ? "/studio" : "/register")}>{isAr ? "ابدأ الآن" : "Start now"} <ArrowRight size={16} /></Button>
              <GooglePlayButton href={PLAY_URL} />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-10">
        <p className="abl-eyebrow mb-3">{isAr ? "14 نوع مبنى" : "14 building types"}</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {TYPOLOGIES.map(([e, en, ar], i) => (
            <Card key={i} className="flex flex-col items-center gap-2 p-4 transition-transform hover:-translate-y-1">
              <span className="text-3xl">{e}</span><span className="text-center text-xs font-bold">{isAr ? ar : en}</span>
            </Card>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-10">
        <div className="grid gap-6 md:grid-cols-3">
          <div>
            <p className="abl-eyebrow mb-3 flex items-center gap-1.5"><ShieldCheck size={13} /> {isAr ? "الأكواد" : "Codes"}</p>
            <div className="flex flex-wrap gap-2">{CODES.map((c) => <span key={c} className="rounded-lg border border-border bg-card/60 px-3 py-1.5 text-xs font-bold">{c}</span>)}</div>
          </div>
          <div>
            <p className="abl-eyebrow mb-3 flex items-center gap-1.5"><Download size={13} /> {isAr ? "التصدير" : "Exports"}</p>
            <div className="flex flex-wrap gap-2">{EXPORTS.map((c) => <span key={c} className="rounded-lg border border-border bg-card/60 px-3 py-1.5 text-xs font-bold">{c}</span>)}</div>
          </div>
          <div>
            <p className="abl-eyebrow mb-3 flex items-center gap-1.5"><Globe size={13} /> {isAr ? "اللغات" : "Languages"}</p>
            <div className="flex flex-wrap gap-2">{LANGUAGES.slice(0, 8).map((l) => <span key={l.code} className="rounded-lg border border-border bg-card/60 px-2.5 py-1.5 text-xs font-bold">{l.flag} {l.nativeName}</span>)}<span className="rounded-lg border border-border bg-card/60 px-2.5 py-1.5 text-xs font-bold">+{LANGUAGES.length - 8}</span></div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-10">
        <p className="abl-eyebrow mb-3">{isAr ? "الأسئلة الشائعة" : "FAQ"}</p>
        <h2 className="mb-5 max-w-3xl text-2xl font-black sm:text-3xl">{isAr ? "أسئلة شائعة عن المخططات والتصدير" : "Common questions about the plans & exports"}</h2>
        <div className="grid gap-3 md:grid-cols-2">
          {FAQS.map(([qEn, qAr, aEn, aAr], i) => (
            <div key={i} className="rounded-xl border border-border bg-card/60 p-5" data-testid={`showcase-faq-${i}`}>
              <h3 className="text-base font-black">{isAr ? qAr : qEn}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{isAr ? aAr : aEn}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
        <div className="mb-2 flex flex-wrap justify-center gap-4">
          <Link to="/landing" className="hover:text-foreground">{isAr ? "الرئيسية" : "Home"}</Link>
          <Link to="/methodology" className="hover:text-foreground">{isAr ? "كيف يعمل" : "How it works"}</Link>
          <Link to="/contact" className="hover:text-foreground">{isAr ? "تواصل" : "Contact"}</Link>
          <Link to="/legal/privacy" className="hover:text-foreground">{isAr ? "الخصوصية" : "Privacy"}</Link>
          <Link to="/legal/terms" className="hover:text-foreground">{isAr ? "الشروط" : "Terms"}</Link>
        </div>
        © 2026 AutoBIM Logic. Automate BIM. Engineer with Logic.
      </footer>
    </div>
  );
}
